import { useState, useEffect, useCallback } from 'react';
import type { PlantPhoto } from '../types/photo';
import { subscribeToPhotos } from '../services/photos';

interface UsePhotosResult {
  photos: PlantPhoto[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to subscribe to a plant's photo gallery (real-time updates).
 * Photos come back oldest first, so the strip reads as a growth timeline.
 */
export function usePhotos(userId: string | null, plantId: string | null): UsePhotosResult {
  const [photos, setPhotos] = useState<PlantPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!userId || !plantId) {
      setPhotos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToPhotos(
      userId,
      plantId,
      (newPhotos) => {
        setPhotos(newPhotos);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load photos');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, plantId, refreshKey]);

  return { photos, loading, error, refetch };
}
